import React, { useState } from 'react';
import { Typography, Fade, Card, CardContent, Box, Chip, Button, Grid } from '@mui/material';
import { styled } from '@mui/material/styles';
import JobModal from '../../components/modals/profil/JobModal';
import HeaderTop from "../../components/partials/HeaderTop";
import Banner from "../../components/partials/Banner";

// Styled components
const MainContainer = styled('div')({
    padding: '20px',
    backgroundColor: '#f8f9fa',
    minHeight: '100vh'
});

const ProfileCard = styled(Card)({
    backgroundColor: 'white',
    borderRadius: '12px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
    padding: '20px',
    marginBottom: '20px'
});

const ContractCard = styled(Card)(({ theme }) => ({
    backgroundColor: 'white',
    borderRadius: '16px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    height: '350px',
    position: 'relative',
    overflow: 'visible',
    transition: 'transform 0.2s, box-shadow 0.2s',
    '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: '0 4px 15px rgba(0,0,0,0.12)'
    },
    [theme.breakpoints.down('sm')]: {
        height: '300px'
    }
}));

const CompanyLogo = styled('img')({
    width: '60px',
    height: '60px',
    borderRadius: '12px',
    objectFit: 'cover',
    position: 'absolute',
    top: '-20px',
    left: '20px',
    border: '3px solid white',
    boxShadow: '0 2px 5px rgba(0,0,0,0.1)'
});

const ContractHeader = styled(Box)({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '12px',
    paddingTop: '30px'
});

const CompanyInfo = styled(Box)({
    display: 'flex',
    flexDirection: 'column',
    marginLeft: '70px'
});

const ContractDetails = styled(Box)({
    marginBottom: '16px',
    height: '120px',
    overflow: 'hidden'
});

const ContractFooter = styled(Box)({
    position: 'absolute',
    bottom: '20px',
    left: '20px',
    right: '20px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
});

const StatusChip = styled(Chip)(({ status }) => ({
    fontSize: '12px',
    height: '24px',
    fontWeight: 'bold',
    ...(status === 'new' && {
        backgroundColor: '#4caf50',
        color: 'white'
    }),
    ...(status === 'internship' && {
        backgroundColor: '#ff9800',
        color: 'white'
    })
}));

const BrowseButton = styled(Button)({
    textTransform: 'none',
    fontWeight: 'bold',
    borderRadius: '20px',
    padding: '6px 16px',
    backgroundColor: '#2196f3',
    color: 'white',
    '&:hover': {
        backgroundColor: '#1976d2'
    }
});

// Mock data avec description et requirements
const mockContracts = [
    {
        id: 1,
        title: 'Senior Web Designer, Developer',
        company: 'COMPANY',
        logo: 'https://randomuser.me/api/portraits/lego/0.jpg',
        location: '1363-1385 Sunset Blvd Los Angeles, CA 90026, USA',
        website: 'thewebmax.com',
        salary: '$2500 / Month',
        postedDate: '1 days ago',
        status: 'new',
        description: 'Nous recherchons un designer/développeur web senior pour rejoindre notre équipe créative. Vous serez responsable de la conception et du développement de sites web modernes et réactifs.',
        requirements: [
            '5+ ans d\'expérience en design et développement web',
            'Maîtrise de HTML, CSS, JavaScript et React',
            'Expérience avec Figma ou Adobe XD',
            'Connaissance des bonnes pratiques SEO',
            'Capacité à travailler en équipe'
        ]
    },
    {
        id: 2,
        title: 'Senior Rolling Stock Technician',
        company: 'BUSINESS',
        logo: 'https://randomuser.me/api/portraits/lego/1.jpg',
        location: '1363-1385 Sunset Blvd Los Angeles, CA 90026, USA',
        website: 'thewebmax.com',
        salary: '$7 / Hour',
        postedDate: '15 days ago',
        status: 'internship',
        description: 'Technicien senior en matériel roulant recherché pour assurer la maintenance et la réparation de notre flotte de véhicules ferroviaires.',
        requirements: [
            'Certificat en maintenance ferroviaire',
            '3 ans d\'expérience minimum',
            'Connaissance des systèmes électriques et mécaniques',
            'Capacité à travailler en équipe',
            'Disponibilité pour les horaires variables'
        ]
    },
    {
        id: 3,
        title: 'Full Stack Developer',
        company: 'TECH CORP',
        logo: 'https://randomuser.me/api/portraits/lego/2.jpg',
        location: '1234 Tech Street San Francisco, CA 94105, USA',
        website: 'techcorp.com',
        salary: '$4500 / Month',
        postedDate: '3 days ago',
        status: 'new',
        description: 'Développeur Full Stack pour travailler sur nos applications web et mobiles. Vous participerez à toutes les phases de développement, du front-end au back-end.',
        requirements: [
            'Expérience avec React et Node.js',
            'Connaissance des bases de données SQL et NoSQL',
            'Expérience avec les API REST',
            'Capacité à écrire du code propre et documenté',
            'Expérience avec Git'
        ]
    },
    {
        id: 4,
        title: 'UI/UX Designer',
        company: 'CREATIVE STUDIO',
        logo: 'https://randomuser.me/api/portraits/lego/3.jpg',
        location: '567 Design Ave New York, NY 10001, USA',
        website: 'creativestudio.com',
        salary: '$3200 / Month',
        postedDate: '7 days ago',
        status: 'internship',
        description: 'Designer UI/UX pour créer des interfaces utilisateur intuitives et esthétiques pour nos clients. Vous travaillerez en étroite collaboration avec les développeurs et les chefs de produit.',
        requirements: [
            'Portfolio démontrant vos compétences en design',
            'Maîtrise de Figma ou Adobe XD',
            'Compréhension des principes UX',
            'Capacité à travailler sur plusieurs projets simultanément',
            'Bonne communication'
        ]
    }
];

const ProContractsPage = () => {
    const [openModal, setOpenModal] = useState(false);
    const [selectedContract, setSelectedContract] = useState(null);

    const handleOpenModal = (contract) => {
        setSelectedContract(contract);
        setOpenModal(true);
    };

    const handleCloseModal = () => {
        setOpenModal(false);
    };

    return (
        <>
            <HeaderTop />
            <Banner
                title="Mes Contrats"
                backgroundImage="https://images.unsplash.com/photo-1581578731548-c64695cc6952?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80"
            />
            <MainContainer>
            <Fade in timeout={1000}>
                <ProfileCard>
                    <CardContent sx={{ padding: '0 !important' }}>
                        <Typography
                            variant="h4"
                            sx={{
                                fontWeight: 'bold',
                                marginBottom: '30px',
                                color: '#333'
                            }}
                        >
                            Contrats Professionnels
                        </Typography>

                        <Grid container spacing={3}>
                            {mockContracts.map((contract) => (
                                <Grid item xs={12} sm={6} md={6} lg={6} key={contract.id}>
                                    <ContractCard>
                                        <CardContent sx={{ height: '100%', position: 'relative' }}>
                                            <CompanyLogo
                                                src={contract.logo}
                                                alt={`${contract.company} logo`}
                                            />

                                            <ContractHeader>
                                                <CompanyInfo>
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            color: '#666',
                                                            fontSize: '12px',
                                                            marginBottom: '2px'
                                                        }}
                                                    >
                                                        {contract.company}
                                                    </Typography>
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            color: '#999',
                                                            fontSize: '11px'
                                                        }}
                                                    >
                                                        {contract.postedDate}
                                                    </Typography>
                                                </CompanyInfo>

                                                <StatusChip
                                                    label={contract.status === 'new' ? 'New' : 'Intership'}
                                                    status={contract.status}
                                                />
                                            </ContractHeader>

                                            <Box sx={{ marginBottom: '12px' }}>
                                                <Typography
                                                    variant="h6"
                                                    sx={{
                                                        fontWeight: 'bold',
                                                        fontSize: '18px',
                                                        color: '#333',
                                                        marginBottom: '8px'
                                                    }}
                                                >
                                                    {contract.title}
                                                </Typography>
                                            </Box>

                                            <ContractDetails>
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        color: '#666',
                                                        marginBottom: '4px',
                                                        fontSize: '13px'
                                                    }}
                                                >
                                                    {contract.location}
                                                </Typography>

                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        color: '#2196f3',
                                                        fontSize: '13px',
                                                        textDecoration: 'none',
                                                        cursor: 'pointer',
                                                        '&:hover': {
                                                            textDecoration: 'underline'
                                                        }
                                                    }}
                                                    component="a"
                                                    href={`https://${contract.website}`}
                                                    target="_blank"
                                                >
                                                    {contract.website}
                                                </Typography>
                                            </ContractDetails>

                                            <ContractFooter>
                                                <Typography
                                                    variant="h6"
                                                    sx={{
                                                        fontWeight: 'bold',
                                                        color: '#333',
                                                        fontSize: '16px'
                                                    }}
                                                >
                                                    {contract.salary}
                                                </Typography>

                                                <BrowseButton
                                                    variant="contained"
                                                    onClick={() => handleOpenModal(contract)}
                                                >
                                                    Voir le contrat
                                                </BrowseButton>
                                            </ContractFooter>
                                        </CardContent>
                                    </ContractCard>
                                </Grid>
                            ))}
                        </Grid>
                    </CardContent>
                </ProfileCard>
            </Fade>

            <JobModal
                open={openModal}
                handleClose={handleCloseModal}
                job={selectedContract}
            />
        </MainContainer>
        </>
    );
};

export default ProContractsPage;